// SmartImg — drop-in <img> replacement that auto-serves a WebP source when
// a sibling .webp file is known to exist on disk (per webpManifest).
//
// Why the manifest? Without it, <picture><source srcSet="…/foo.webp">…</picture>
// would still "succeed" for missing .webp files in production because Cloudflare
// + the SPA fallback return `index.html` (text/html, HTTP 200) for unknown
// asset paths. The browser then can't decode the HTML as WebP and renders
// nothing — and crucially, the <img> child is NOT used as a fallback (from
// the browser's POV the <source> succeeded). Result: silent blank images.
//
// Notes:
//  - Only swaps when src is a local /assets path with .png/.jpg/.jpeg suffix.
//  - The .webp companion must be present in WEBP_MANIFEST (generated by
//    /app/scripts/convert_to_webp.py).
//  - Honours all standard <img> props (alt, className, style, loading, etc.).
//  - srcSet is intentionally not handled here; this is for single-src cases.
import React from "react";
import { WEBP_MANIFEST } from "../../data/webpManifest";

const RE = /^(\/assets\/[^?#]+)\.(png|jpe?g)(\?[^#]*)?(#.*)?$/i;

const toWebp = (src) => {
  if (!src || typeof src !== "string") return null;
  const m = src.match(RE);
  if (!m) return null;
  const candidate = `${m[1]}.webp`;
  if (!WEBP_MANIFEST.has(candidate)) return null;
  return `${candidate}${m[3] || ""}${m[4] || ""}`;
};

const SmartImg = React.forwardRef(function SmartImg(
  { src, alt = "", ...rest },
  ref
) {
  const webp = toWebp(src);
  if (!webp) {
    return <img ref={ref} src={src} alt={alt} {...rest} />;
  }
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img ref={ref} src={src} alt={alt} {...rest} />
    </picture>
  );
});

export default SmartImg;
